import { Typography } from '@mui/material'
import {
  ChangeEvent,
  ChangeEventHandler,
  Dispatch,
  FC,
  FormEvent,
  useCallback,
  useState
} from 'react'
import { AuthApi } from 'src/api'
import { Button, Input, RequiredAsterisk, useAnalytic } from 'src/components'
import { ETrackingEvent } from 'src/enums'
import {
  useDebounceAnalyticInput,
  useQueryParams,
  useValidation
} from 'src/hooks'
import { Logo } from 'src/images'
import { EAuthView, ICompanyUserModel } from 'src/interfaces'
import { registerPasswordSchema } from 'src/validation'
import Style from './style.module.scss'

interface ISetAccountInformation {
  companyName: string
  companyUrl: string
  companyZipcode?: string
  isRecruiter?: boolean
  userType?: string
  recruiterRoles?: string[]
  recruiterCompanies?: ICompanyUserModel['recruiterCompanies']
  password: string
  confirmPassword: string
}

type TSignUpPayload = Parameters<typeof AuthApi.signUpProfile>[0]

interface IProps {
  setView: Dispatch<EAuthView>
  signUpInfo: TSignUpPayload
  onSubmit: (
    data: Omit<ISetAccountInformation, 'confirmPassword'>
  ) => Promise<void>
  companyName?: string
  companyUrl?: string
  companyZipcode?: string
}

export const SetPassword: FC<IProps> = ({
  onSubmit,
  companyName,
  companyUrl,
  companyZipcode
}) => {
  const { eventHandler } = useAnalytic('signup2')
  const { analyticInput } = useDebounceAnalyticInput('signup2')
  const { isRecruiter } = useQueryParams()
  const [loading, setLoading] = useState(false)
  const [formValues, setFormValues] = useState<ISetAccountInformation>({
    isRecruiter: isRecruiter === 'true',
    companyName: companyName || '',
    companyUrl: companyUrl || '',
    companyZipcode: companyZipcode || '',
    recruiterCompanies: [
      { name: '', website: '' },
      { name: '', website: '' }
    ],
    recruiterRoles: ['', '', '', ''],
    userType: '',
    password: '',
    confirmPassword: ''
  })
  const { errors, validate } = useValidation({
    data: formValues,
    schema: registerPasswordSchema
  })

  const updateRecruiterCompanies = useCallback((e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value } = e.target
    setFormValues((prev) => ({
      ...prev,
      recruiterCompanies: prev.recruiterCompanies?.map((company, i) => i === index ? { ...company, [name]: value } : company)
    }))
  }, [analyticInput])

  const updateRecruiterRoles = useCallback((e: ChangeEvent<HTMLInputElement>, index: number) => {
    const { value } = e.target
    setFormValues((prev) => ({
      ...prev,
      recruiterRoles: prev.recruiterRoles?.map((role, i) => i === index ? value : role)
    }))
  }, [])

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      const { name, value } = e.target

      if (name === 'companyName') {
        analyticInput(ETrackingEvent.INPUT_COMPANY_NAME, value)
      }

      if (name === 'companyUrl') {
        analyticInput(ETrackingEvent.INPUT_COMPANY_URL, value)
      }

      if (name === 'companyZipcode' && !value.match(/^\d{0,9}$/)) {
        return
      }

      setFormValues((prev) => ({
        ...prev,
        [name]: value
      }))
    },
    [analyticInput]
  )

  const onNext = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const { isValid } = await validate()
      if (!isValid) {
        return
      }

      setLoading(true)

      const values = {
        ...formValues,
        recruiterCompanies: formValues.isRecruiter ? formValues.recruiterCompanies?.filter((company) => company.name && company.website) : undefined,
        recruiterRoles: formValues.isRecruiter ? formValues.recruiterRoles?.filter((role) => role) : undefined,
        companyZipcode: !formValues.isRecruiter ? formValues.companyZipcode : undefined
      }

      onSubmit(values).finally(() => setLoading(false))
    },
    [formValues, onSubmit, validate]
  )

  return (
    <form className={Style.STForm} onSubmit={onNext}>
      <img src={Logo} alt="logo" width={48}/>
      <div className={Style.LoginHeader}>
        <Typography className={Style.Title}>
          Enter Your Account Information
        </Typography>
      </div>
      <div className={Style.FormInput}>
        <Input
          label={<>Company<RequiredAsterisk/></>}
          id="companyName"
          name="companyName"
          disabled={!!companyName}
          value={formValues.companyName || ''}
          onChange={handleChange}
          error={errors.getError('companyName')}
        />
        <Input
          label={<>Company URL<RequiredAsterisk/></>}
          id="companyUrl"
          name="companyUrl"
          value={formValues.companyUrl || ''}
          disabled={!!companyUrl}
          onChange={handleChange}
          error={errors.getError('companyUrl')}
        />
        {formValues?.isRecruiter
          ? (
            <div className="fx-column gap-6">
              <div className="fx-column gap-2">
                <div className="bold-14 txt-neutral-500">
                  What Companies Do You Currently Recruit For?<RequiredAsterisk/>
                </div>
                <div className="fx-column gap-2">
                  {formValues.recruiterCompanies?.map((company, index) => (
                    <div className="fx gap-2" key={index}>
                      <Input
                        id={`recruiterCompanies-${index}-name`}
                        name="name"
                        value={company.name || ''}
                        onChange={(e) => updateRecruiterCompanies(e, index)}
                        error={errors.getError(`recruiterCompanies[${index}].name`)}
                        wrapperClassName="w-full"
                        placeholder="Company Name"
                      />
                      <Input
                        id={`recruiterCompanies-${index}-website`}
                        name="website"
                        value={company.website || ''}
                        wrapperClassName="w-full"
                        onChange={(e) => updateRecruiterCompanies(e, index)}
                        error={errors.getError(`recruiterCompanies[${index}].website`)}
                        placeholder="Company URL"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="fx-column gap-2">
                <div className="bold-14 txt-neutral-500">
                  What Roles Do You Specialize In?<RequiredAsterisk/>
                </div>
                <div className="fx-column gap-2">
                  <div className={Style.recruiterRoles}>
                    {formValues.recruiterRoles?.map((role, index) => (
                      <Input
                        key={index}
                        name="role"
                        value={role || ''}
                        error={errors.getError(`recruiterRoles[${index}]`)}
                        wrapperClassName="w-full"
                        onChange={(e) => updateRecruiterRoles(e, index)}
                        placeholder="Role"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )
          : (
            <>
              <Input
                label={<>Company HQ Zipcode<RequiredAsterisk/></>}
                id="companyZipcode"
                name="companyZipcode"
                placeholder="e.g., 90210"
                disabled={!!companyZipcode}
                value={formValues.companyZipcode || ''}
                onChange={handleChange}
                error={errors.getError('companyZipcode')}
              />
              <Input
                label={<>Position<RequiredAsterisk/></>}
                id="userType"
                name="userType"
                value={formValues.userType || ''}
                onChange={handleChange}
                error={errors.getError('userType')}
              />
            </>
          )}

        <Input
          label={<>Password<RequiredAsterisk/></>}
          id="password"
          type="password"
          name="password"
          value={formValues.password || ''}
          onChange={handleChange}
          onClick={eventHandler(ETrackingEvent.INPUT_PASSWORD)}
          error={errors.getError('password')}
          isCheckPassword
        />
        <Input
          label={<>Confirm your password<RequiredAsterisk/></>}
          id="confirmPassword"
          type="password"
          name="confirmPassword"
          value={formValues.confirmPassword || ''}
          onChange={handleChange}
          onClick={eventHandler(ETrackingEvent.INPUT_CONFIRM_PASSWORD)}
          error={errors.getError('confirmPassword')}
        />

        <Button
          type="submit"
          className="round-16"
          disabled={errors.hasError() || loading}
          onClick={eventHandler(ETrackingEvent.BTN_CREATE_ACCOUNT)}
        >
          create account
        </Button>
      </div>
    </form>
  )
}
